import {useEffect, useState} from "react";
import {useSearchParams} from "react-router";

import {LimitAndOffset} from "@/components/table/subComponents/Paginator/Paginator.tsx";
import {useUiSettings} from "@/lib/hooks/useUiSettings.ts";

export const DEFAULT_OFFSET_KEY = "offset";

export interface UsePaginationOptions {
  offsetKey?: string;
  limitKey?: string;
  defaultLimit?: number;
  defaultOffset?: number;
  useUrlParams?: boolean;
  onPaginationChange?: (pagination: LimitAndOffset, params: URLSearchParams) => void;
}

export const usePagination = ({
  offsetKey = DEFAULT_OFFSET_KEY,
  limitKey = "limit",
  defaultLimit,
  defaultOffset = 0,
  useUrlParams = true,
  onPaginationChange,
}: UsePaginationOptions = {}) => {
  const [queryParams, setQueryParams] = useSearchParams();
  const {uiSettings} = useUiSettings();
  const effectiveDefaultLimit = defaultLimit ?? uiSettings.table_page_size;

  const urlOffset = useUrlParams ? parseInt(queryParams.get(offsetKey)!) || defaultOffset : defaultOffset;
  const urlLimit = useUrlParams ? parseInt(queryParams.get(limitKey)!) || effectiveDefaultLimit : effectiveDefaultLimit;

  const [pagination, setPaginationState] = useState<LimitAndOffset>(() => ({
    offset: urlOffset,
    limit: urlLimit,
  }));

  useEffect(() => {
    if (!useUrlParams) {
      return;
    }
    if (pagination.offset !== urlOffset || pagination.limit !== urlLimit) {
      setPaginationState({offset: urlOffset, limit: urlLimit});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pagination intentionally excluded to prevent infinite loops; this effect syncs URL → state
  }, [urlOffset, urlLimit, useUrlParams]);

  const setPagination = (newPagination: LimitAndOffset, externalParams?: URLSearchParams) => {
    const params = externalParams ?? (useUrlParams ? new URLSearchParams(queryParams) : null);
    if (params) {
      params.set(offsetKey, newPagination.offset.toString());
      params.set(limitKey, newPagination.limit.toString());
    }
    onPaginationChange?.(newPagination, params ?? new URLSearchParams());
    setPaginationState(newPagination);
    if (params && !externalParams) {
      setQueryParams(params);
    }
  };

  return {
    pagination,
    setPagination,
    offsetKey,
  };
};
